import React, { useEffect, useId, useRef, useState } from 'react'
import classNames from 'classnames'
import { motion, useReducedMotion } from 'framer-motion'
import useGlassLens from '@/hooks/useGlassLens'
import LensFilter from '@/components/ui/lens-filter'

import './SegmentedToggle.scss'

/** Outer dimensions mirror ButtonIcon, so a toggle sits flush beside one in a
 *  toolbar row. The icon stays 16px at every size; only the target grows. */
const ICON_PX = 16
const SIZE_CONFIG = {
  small: 32,
  medium: 40,
  large: 48,
}
// The track's stroke is an inset ring (box-shadow), not a border, so it takes no
// room and the segments only need to clear the padding.
const PADDING_PX = 4
const GAP_PX = 2

/** Ceiling for the lens pause. The move normally ends it, but a selection the host
 *  declines never moves the thumb, and the lens must not stay off. */
const LENS_PAUSE_TIMEOUT_MS = 700

/* A straight slide with no overshoot, so the thumb stops on its segment. */
const THUMB_TRANSITION = { type: 'tween', duration: 0.2, ease: [0.2, 0, 0, 1] }

/* Off the radius scale on purpose — `--radius-xs` is 4 and `--radius-sm` 8, and the thumb
   wants the step between them. The lens below reads the same value, or the glass would bend
   its backdrop around a corner the thumb does not have. */
const THUMB_RADIUS_PX = 6

// The resting thumb refracts its backdrop like the tab bar's focus pill: depth is
// the refracting band in px (never more than the pill's 16, and scaled down with
// the thumb so a small one keeps a flat centre), scale how far a rim pixel
// reaches, radius the thumb's own corner. The lens is dropped while the thumb
// travels, since a backdrop filter smears on a moving element.
const THUMB_LENS = { rim: 16, scale: 28, cornerRadius: THUMB_RADIUS_PX }
const THUMB_RIM_SHARE = 0.3

const segmentSize = size => (SIZE_CONFIG[size] ?? SIZE_CONFIG.small) - 2 * PADDING_PX

/**
 * Icon segmented control: one option at a time, with the selection carried by a
 * sliding thumb rather than by each segment repainting.
 *
 * @param {array} options - `{ value, Icon, label }`; `label` is the accessible name and tooltip
 * @param {*} value - Currently selected option value
 * @param {function} onChange - Receives the newly selected value
 * @param {string} size - `small` | `medium` | `large`, matching ButtonIcon
 * @param {string} ariaLabel - Names the group as a whole
 */
const SegmentedToggle = ({ options = [], value, onChange = () => {}, size = 'medium', ariaLabel, className = '' }) => {
  const [lensPaused, setLensPaused] = useState(false)
  const lensPauseTimeout = useRef(null)
  const prefersReducedMotion = useReducedMotion()

  const segmentPx = segmentSize(size)
  const surfaceRef = useRef(null)
  const lens = useGlassLens(surfaceRef, {
    rim: Math.min(THUMB_LENS.rim, Math.round(segmentPx * THUMB_RIM_SHARE)),
    cornerRadius: THUMB_LENS.cornerRadius,
  })
  // One filter per instance; useId's separators are not valid in a url(#…).
  const lensId = `segmented-toggle-lens-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`

  useEffect(() => () => clearTimeout(lensPauseTimeout.current), [])

  const resumeLens = () => {
    clearTimeout(lensPauseTimeout.current)
    setLensPaused(false)
  }

  const activeIndex = Math.max(
    options.findIndex(option => option.value === value),
    0
  )

  const handleSelect = nextValue => {
    if (nextValue === value) return
    setLensPaused(true)
    clearTimeout(lensPauseTimeout.current)
    lensPauseTimeout.current = setTimeout(() => setLensPaused(false), LENS_PAUSE_TIMEOUT_MS)
    onChange(nextValue)
  }

  if (options.length === 0) return null

  const lensActive = !!lens && !lensPaused

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={classNames('segmented-toggle relative inline-flex shrink-0 items-center rounded-lg', className)}
      style={{ gap: GAP_PX, padding: PADDING_PX }}
    >
      <motion.span
        aria-hidden
        className="segmented-toggle__thumb"
        style={{ width: segmentPx, height: segmentPx, top: PADDING_PX, left: PADDING_PX }}
        animate={{ x: activeIndex * (segmentPx + GAP_PX) }}
        initial={false}
        transition={prefersReducedMotion ? { duration: 0 } : THUMB_TRANSITION}
        onAnimationComplete={resumeLens}
      >
        <span
          ref={surfaceRef}
          className="segmented-toggle__surface"
          style={
            lensActive ? { backdropFilter: `url(#${lensId})`, WebkitBackdropFilter: `url(#${lensId})` } : undefined
          }
        >
          {lens && <LensFilter id={lensId} lens={lens} scale={THUMB_LENS.scale} />}
        </span>
      </motion.span>

      {options.map(({ value: optionValue, Icon, label }) => {
        const active = optionValue === value
        return (
          <button
            key={optionValue}
            type="button"
            onClick={() => handleSelect(optionValue)}
            aria-pressed={active}
            aria-label={label}
            title={label}
            className={classNames(
              'segmented-toggle__segment relative z-[1] flex cursor-pointer items-center justify-center border-none bg-transparent p-0 text-Neutral-6 outline-offset-2 transition-colors focus-visible:outline-[var(--color-brand-04)]',
              /* The selected segment sits over the thumb, which is its fill already. The
                 fill is ButtonIcon's, which reads on the page ground in both themes. */
              !active && 'hover:bg-[var(--button-icon-hover-fill)]'
            )}
            style={{ width: segmentPx, height: segmentPx, borderRadius: THUMB_RADIUS_PX }}
          >
            <Icon width={ICON_PX} height={ICON_PX} />
          </button>
        )
      })}
    </div>
  )
}

export default SegmentedToggle
